import React from 'react';
import { config, styled } from '../../../stitches.config';
import { TinaField } from '../../../shared/types';
import { Button } from '../../atoms/Button/Button';
import ExportedImage from 'next-image-export-optimizer';
import { useRouter } from 'next/router';
import { Flex } from '../../atoms/Flex';
import { Spacer } from '../../atoms/Spacer';
import { Headline } from '../../molecules/Headline/Headline';
import { useEditState } from 'tinacms/dist/react';
import { removeAssetTinaIoFromImageUrl } from '../../../shared/Helper';

export type Cover1Props = {
  headline?: string | null;
  subHeadline?: string | null;
  buttonLabel?: string | null;
  buttonHref?: string | null;
  imageSrc?: string | null;
  imageAlt?: string | null;
  imagePosition?: string | null;
  textPosition?: 'left' | 'right' | null;
  children?: React.ReactNode;
} & TinaField;

const ImageLayout = styled('div', {
  width: '100%',
  position: 'relative',
  minHeight: '200px',
  '@tabletAndUp': {
    minHeight: '300px',
  },
  '@laptopAndUp': {
    minHeight: '480px',
  },
});

export const Cover1: React.FC<Cover1Props> = ({
  headline,
  subHeadline,
  buttonLabel,
  buttonHref,
  imageSrc,
  imageAlt,
  textPosition = 'right',
  imagePosition,
  children,
}) => {
  const router = useRouter();
  const { edit: isInAdminEditMode } = useEditState();
  const imageSrcWithoutAssetTinaIo = removeAssetTinaIoFromImageUrl(
    imageSrc ?? '',
  );

  return (
    <>
      <Flex
        css={{
          backgroundColor: '$skin-white-smoke',
          flexDirection: 'column',
          paddingBlock: '$spacing-5x',
          paddingInline: '$spacingMainMobile',
          '@laptopAndUp': {
            paddingBlock: '$spacing-7x',
            position: 'relative',
          },
        }}
      >
        <Flex
          css={{
            flexDirection: 'column',
            maxWidth: '$laptop',
            width: '100%',
            margin: 'auto',
            gap: '$spacing-3x',
            '@laptopAndUp': {
              display: 'grid',
              gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
              alignItems: 'center',
              gap: 0,
            },
          }}
        >
          {imageSrc && imageAlt && isInAdminEditMode && (
            <>
              <ImageLayout>
                <ExportedImage
                  src={imageSrc}
                  alt={imageAlt}
                  layout="fill"
                  objectFit="cover"
                  objectPosition={imagePosition ? imagePosition : undefined}
                  sizes={`${config.media.laptopAndUp} 50vw, 100vw`}
                />
              </ImageLayout>
            </>
          )}
          {imageSrc && imageAlt && !isInAdminEditMode && (
            <>
              <ImageLayout>
                <ExportedImage
                  src={imageSrcWithoutAssetTinaIo}
                  alt={imageAlt}
                  layout="fill"
                  objectFit="cover"
                  objectPosition={imagePosition ? imagePosition : undefined}
                  sizes={`${config.media.laptopAndUp} 50vw, 100vw`}
                />
              </ImageLayout>
            </>
          )}
          <Flex
            css={{
              flexDirection: 'column',
              zIndex: '$higher',
              '@laptopAndUp': {
                maxWidth: 'calc($textContent - 80px)',
                paddingLeft: '$spacing-14x',
              },
            }}
          >
            <Headline headline={headline} subheadline={subHeadline} />
            {children}
            <Spacer size="$spacing-4x" />
            {(buttonLabel || buttonHref) && (
              <Button
                fullWidthOnMobile
                onClick={() => {
                  if (buttonHref) {
                    router.push(buttonHref);
                  }
                }}
              >
                {buttonLabel}
              </Button>
            )}
          </Flex>
        </Flex>
      </Flex>
    </>
  );
};
